/**
 * MFA (2FA) Service – email OTP for login.
 * Calls Supabase Edge Functions request-mfa-code and verify-mfa-code.
 */

const getFunctionsUrl = (): string => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new Error('VITE_SUPABASE_URL is not set');
  return String(url).replace(/\/$/, '');
};

export const mfaService = {
  async requestCode(accessToken: string, options?: { setup?: boolean; settings?: boolean }): Promise<void> {
    const base = getFunctionsUrl();
    const intent = options?.setup ? 'setup' : options?.settings ? 'settings' : undefined;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 18_000);
    let res: Response;
    try {
      res = await fetch(`${base}/functions/v1/request-mfa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(intent ? { intent } : {}),
        signal: controller.signal,
      });
    } catch (e: unknown) {
      clearTimeout(timeout);
      if (e instanceof Error && e.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      throw e;
    }
    clearTimeout(timeout);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to send MFA code');
    }
    if (!data.success) {
      throw new Error(data.error || 'Failed to send MFA code');
    }
  },

  /** Login flow: email + code, returns token for verifyOtp */
  async verifyCode(email: string, code: string): Promise<{ token: string }> {
    const base = getFunctionsUrl();
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!anonKey) {
      throw new Error('VITE_SUPABASE_ANON_KEY is not set – required for MFA verification');
    }
    const res = await fetch(`${base}/functions/v1/verify-mfa-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ email: email.trim(), code: String(code).replace(/\s/g, '').slice(0, 6) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Verification failed');
    }
    if (!data.success || !data.token) {
      throw new Error(data.error || 'Verification failed');
    }
    return { token: data.token };
  },

  /** Settings flow: session token + code, no token returned (user already logged in). mfa_enabled = new state so backend can send confirmation email. */
  async verifyCodeForSettings(accessToken: string, code: string, mfaEnabled: boolean): Promise<void> {
    const base = getFunctionsUrl();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    let res: Response;
    try {
      res = await fetch(`${base}/functions/v1/verify-mfa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          code: String(code).replace(/\s/g, '').slice(0, 6),
          mfa_enabled: mfaEnabled,
        }),
        signal: controller.signal,
      });
    } catch (e: unknown) {
      clearTimeout(timeout);
      if (e instanceof Error && e.name === 'AbortError') {
        throw new Error('Verification timed out. Please try again.');
      }
      throw new Error((e as Error)?.message || 'Verification failed');
    }
    clearTimeout(timeout);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Verification failed');
    }
    if (!data.success) {
      throw new Error(data.error || 'Verification failed');
    }
  },
};
